// src/store/profileSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "./apiClient"; // central axios instance

export const fetchUserProfile = createAsyncThunk(
  "profile/fetchUser",
  async (_, { rejectWithValue }) => {
    const raw = localStorage.getItem("user");
    if (!raw) return rejectWithValue("No user in localStorage");

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return rejectWithValue("Corrupt user in localStorage");
    }

    const { username } = parsed;
    if (!username) return rejectWithValue("Username missing");

    try {
      const { data } = await apiClient.get(`/users/?username=${encodeURIComponent(username)}`);
      if (!Array.isArray(data) || data.length === 0) {
        return rejectWithValue(`No user found for "${username}"`);
      }
      return data[0];
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const changePassword = createAsyncThunk(
  "profile/changePassword",
  async ({ email, newPassword }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.patch("/users/password", { email, newPassword });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateProfileDetails = createAsyncThunk(
  "profile/updateProfile",
  async ({ id, ...updateData }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.put(`/users/${id}`, updateData);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const profileSlice = createSlice({
  name: "profile",
  initialState: {
    user: null,
    loading: false,
    error: null,
    pwChanging: false,
    pwError: null,
    pwSuccess: false,
    updating: false,
    updateSuccess: false,
    updateError: null,
  },
  reducers: {
    clearPasswordState(state) {
      state.pwError = null;
      state.pwSuccess = false;
    },
    clearProfileUpdateState(state) {
      state.updateError = null;
      state.updateSuccess = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch user
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Change password
      .addCase(changePassword.pending, (state) => {
        state.pwChanging = true;
        state.pwError = null;
        state.pwSuccess = false;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.pwChanging = false;
        state.pwSuccess = true;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.pwChanging = false;
        state.pwError = action.payload;
      })

      // Update profile
      .addCase(updateProfileDetails.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateProfileDetails.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.user = action.payload;
      })
      .addCase(updateProfileDetails.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload;
      });
  },
});

export const { clearPasswordState, clearProfileUpdateState } = profileSlice.actions;
export default profileSlice.reducer;
